/**
 * PROC-04 Negation-antithesis scaffolding (INV-PACK-04; change C-PACK-REG).
 *
 * Definition-by-negation before assertion. Events per 1k prose words:
 *   (a) a sentence that begins with "Not <lowercase word>" ("Not X. Y."), with sentence starts from
 *       the new splitter (the pack matched `[.!?] Not` in flattened text);
 *   (b) "not … but" within 60 characters, no `. ; : ! ?` between (as the pack: /\bnot\b[^.;:!?]{0,60}\bbut\b/gi).
 * A construction matched by both (a) and (b) counts twice, as in the pack. Prose register only.
 * Ramp: pack `[humanCeiling 0.5 → 0, machineFloor 1.3 → 100]`. Weight 1.5 × 0.65.
 */
import { safeScan } from '../lexicons/regex-list.ts';
import { nativeMeasure, proseBlocks, rateDef, sentenceRange, wordExposure } from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import { proseSentenceList } from './helpers.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const NOT_BUT = /\bnot\b[^.;:!?]{0,60}\bbut\b/gi;
const NOT_FRAGMENT = /^Not\s+[a-z]/;

export const proc04Def = rateDef({
	id: 'PROC-04',
	name: 'Negation-antithesis scaffolding',
	family: 'RHETORIC',
	features: [
		{ id: 'notFragment', label: 'Sentence-initial "Not …"' },
		{ id: 'notBut', label: '"not … but …"' },
	],
	oneWay: true,
	minExposure: 1500,
	exposureUnit: 'words',
	lang: 'native',
	ramp: [
		[0, 0],
		[0.5, 0],
		[1.3, 100],
	],
	unitScale: 1000,
	unitLabel: 'negation-antithesis constructions per 1k words',
	phi0: 1.5,
	weight: 1.5 * 0.65,
	nd: 0.15,
	changes: ['C-PACK-REG'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const k = new Float64Array(doc.paragraphs.count);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	const sl = proseSentenceList(doc);
	let fragments = 0;
	let notBut = 0;
	for (const b of proseBlocks(doc)) {
		if (b.text.includes('Not')) {
			for (let i = sl.paraFirst[b.p] as number; i < (sl.paraFirst[b.p + 1] as number); i++) {
				const { start, end } = sentenceRange(doc, i);
				if (doc.text.charCodeAt(start) !== 0x4e) continue;
				if (!NOT_FRAGMENT.test(doc.text.slice(start, Math.min(end, start + 48)))) continue;
				fragments++;
				k[b.p] = (k[b.p] as number) + 1;
				ev.add(start, end, 0, 1);
			}
		}
		if (/not/i.test(b.text)) {
			safeScan(NOT_BUT, b.text, b.base, (s, en) => {
				notBut++;
				k[b.p] = (k[b.p] as number) + 1;
				ev.add(s, en, 1, 1);
			});
		}
	}
	return {
		k,
		e,
		evidence: ev,
		summary: [`sentence-initial "Not ..." x${fragments}`, `"not ... but ..." x${notBut}`],
		features: { notFragments: fragments, notBut },
	};
}

export const proc04: ChannelImpl = rateChannel(proc04Def, nativeMeasure(measure));
export { proc04Def as def, proc04 as impl };
